import React from 'react';
import type { PanelCompanyProps } from './PanelCompany.types';
import { BuildingOfficeIcon } from './icons';

/**
 * PanelCompany from the Siesa design system
 *
 * Panel horizontal para mostrar la información principal de la compañía y detalles
 * del documento actual. Contiene secciones separadas por divisores verticales.
 *
 * Implemented best practices:
 * - Modifier order: {responsive}:{dark}:{state}:{utility}
 * - Dark mode with 'class' strategy (darkMode: 'class')
 * - Color tokens consistent with documentation
 * - Type safety with strict TypeScript
 *
 * @see docs/colors.md - Color system
 * @see docs/spacing.md - Spacing system
 *
 * @example
 * ```tsx
 * <PanelCompany
 *   companyName="Empresa Demo"
 *   companyId="NIT: 900.123.456-7"
 *   fields={[...]}
 * />
 * ```
 */
export const PanelCompany: React.FC<PanelCompanyProps> = ({
  companyName,
  companyId,
  fields,
  className = '',
}) => {
  return (
    <div
      className={[
        'flex',
        'flex-row',
        'items-stretch',
        'border',
        'border-border-primary',
        'bg-bg-primary',
        'rounded-xl',
        'overflow-hidden',
        'dark:border-dark-border-primary',
        'dark:bg-dark-bg-primary',
        className,
      ]
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()}
    >
      {/* Primera sección: Header de la compañía */}
      <div className="flex shrink-0 items-center gap-2 bg-gradient-to-r from-tertiary-600 to-tertiary-800 p-2 dark:from-tertiary-600 dark:to-tertiary-800">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 dark:bg-dark-bg-custom">
          <BuildingOfficeIcon className="size-5 text-primary-600 dark:text-dark-content-primary" />
        </div>
        <div className="flex flex-col justify-center">
          <span className="text-xs leading-tight text-primary-50 dark:text-primary-50">
            {companyName}
          </span>
          <span className="text-xs font-bold leading-tight text-zinc-100 dark:text-zinc-100">
            {companyId}
          </span>
        </div>
      </div>

      {/* Otras secciones */}
      {fields.map((field, index) => (
        <React.Fragment key={index}>
          {/* Divisor vertical */}
          <div className="w-px shrink-0 bg-border-primary dark:bg-dark-border-primary" />

          {/* Contenido del campo */}
          <div
            className={`flex flex-1 items-center gap-2 ${
              field.isDocument ? 'p-2' : 'px-4 py-3'
            }`}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-primary dark:bg-dark-bg-primary">
              {React.isValidElement(field.icon) ? (
                React.cloneElement(field.icon as React.ReactElement<{ className?: string }>, {
                  className: 'size-5 text-primary-600 dark:text-primary-300',
                })
              ) : (
                field.icon
              )}
            </div>
            <div className="flex flex-col justify-center gap-0.5">
              <span className="text-xs leading-tight text-content-secondary dark:text-dark-content-secondary">
                {field.label}
              </span>
              <span className="text-sm font-bold leading-tight text-content-primary dark:text-dark-content-primary">
                {field.value}
              </span>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};
